import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { usePaginateQuery } from "@/hooks";
import { adminApi } from "@/lib/api";
import { type Paginate, type Role, type SearchRequest } from "@/types";

export type RoleList = Paginate<Role<true>>;

export const roles = createQueryKeys("admin-roles", {
  all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
  one: (id?: number) => [id],
});

const include = "users";

export const fetchRolesQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...roles.all(page, search),
    queryFn: () =>
      adminApi
        .get<RoleList>("roles", {
          params: {
            page,
            include,
            ...search,
          },
        })
        .then((response) => response.data),
  });

export const useFetchRoles = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchRolesQueryOptions(page, search));

export const fetchRoleQueryOptions = (id?: number) =>
  queryOptions({
    ...roles.one(id),
    queryFn: () =>
      adminApi
        .get<Role<true>>(`roles/${id}`, { params: { include } })
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchRole = (id?: number) =>
  useQuery(fetchRoleQueryOptions(id));

export interface RoleRequest {
  name: string;
  description?: string | null;
  permissions: string[];
}

export const useCreateRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: RoleRequest) =>
      adminApi.post("roles", data).then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: roles.all._def,
      });
    },
  });
};

export const useUpdateRole = (id: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: RoleRequest) =>
      adminApi.put(`roles/${id}`, data).then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: roles.all._def,
      });
      queryClient.invalidateQueries(roles.one(id));
    },
  });
};

export const useCreateOrUpdateRole = (id: number | undefined) => {
  const create = useCreateRole();
  const update = useUpdateRole(id!);
  return id ? update : create;
};

export const useDeleteRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => adminApi.delete(`roles/${id}`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: roles.all._def,
      });
      queryClient.invalidateQueries(roles.one(id));
    },
  });
};
